import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ChartBar, Users, SignOut, GearSix, Truck, MagnifyingGlass, PaperPlaneTilt, ForkKnife, Receipt, Storefront, Printer, WarningOctagon, FileText, CaretDoubleLeft, CaretDoubleRight, UploadSimple, Coffee } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const NAV_SECTIONS = [
  { label: "fichaje", items: [
    { to: "/admin/users", testId: "nav-users", icon: Users, text: "Trabajadores" },
    { to: "/admin/dashboard", testId: "nav-dashboard", icon: ChartBar, text: "Informes" },
    { to: "/admin/settings", testId: "nav-settings", icon: GearSix, text: "Ajustes fichaje" },
  ]},
  { label: "tareas", items: [
    { to: "/admin/tasks", testId: "nav-tasks", icon: GearSix, text: "Ajustes tareas" },
    { to: "/admin/tasks-reports", testId: "nav-tasks-reports", icon: Coffee, text: "Informes tareas" },
  ]},
  { label: "proveedores", items: [
    { to: "/admin/suppliers", testId: "nav-suppliers", icon: Truck, text: "Proveedores" },
    { to: "/admin/invoices", testId: "nav-invoices", icon: FileText, text: "Facturas" },
    { to: "/admin/upload-documents", testId: "nav-upload-documents", icon: UploadSimple, text: "Cargar documentos" },
    { to: "/admin/items", testId: "nav-items", icon: MagnifyingGlass, text: "Buscar artículos" },
  ]},
  { label: "carta digital", items: [
    { to: "/admin/bar", testId: "nav-bar", icon: Storefront, text: "Ajustes bar" },
    { to: "/admin/menu", testId: "nav-menu", icon: ForkKnife, text: "Platos y categorías" },
    { to: "/admin/allergens", testId: "nav-allergens", icon: WarningOctagon, text: "Alérgenos" },
    { to: "/admin/orders", testId: "nav-orders", icon: Receipt, text: "Pedidos" },
  ]},
  { label: "sistema", items: [
    { to: "/admin/notifications", testId: "nav-notifications", icon: PaperPlaneTilt, text: "Avisos" },
    { to: "/admin/printers", testId: "nav-printers", icon: Printer, text: "Impresoras" },
  ]},
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    const stored = localStorage.getItem("admin_sidebar_collapsed");
    if (stored !== null) return stored === "1";
    // Default: collapsed on mobile (< md), expanded on desktop
    return typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  });

  useEffect(() => {
    localStorage.setItem("admin_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  const handleLogout = async () => {
    await logout();
    toast.success("Sesión cerrada");
    navigate("/");
  };

  const linkClass = ({ isActive }) =>
    `flex items-center ${collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-4 py-3"} rounded-xl font-display font-medium transition ${
      isActive
        ? "bg-[var(--primary)] text-white"
        : "text-[var(--text-secondary)] hover:bg-white hover:text-[var(--text-primary)]"
    }`;

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      <aside
        data-testid="admin-sidebar"
        className={`no-print border-r border-[var(--border)] bg-white/50 backdrop-blur-sm flex flex-col transition-[width] duration-200 ${collapsed ? "w-16 p-2" : "w-64 p-6"}`}
      >
        <div className={`flex items-center mb-6 ${collapsed ? "justify-center" : "gap-3 mb-10"}`}>
          <img
            src={process.env.REACT_APP_LOGO_URL}
            alt="Logo"
            className={`rounded-xl object-cover border border-[var(--border)] ${collapsed ? "w-9 h-9" : "w-10 h-10"}`}
          />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl font-bold tracking-tight">Fichaje</div>
              <div className="label-cap">admin</div>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          data-testid="admin-sidebar-toggle"
          className={`self-end mb-2 w-9 h-9 rounded-lg border border-[var(--border)] bg-white/70 flex items-center justify-center hover:bg-white text-[var(--text-secondary)] ${collapsed ? "self-center" : ""}`}
          title={collapsed ? "Expandir menú" : "Ocultar menú"}
        >
          {collapsed ? <CaretDoubleRight size={14} weight="bold" /> : <CaretDoubleLeft size={14} weight="bold" />}
        </button>

        <nav className={`space-y-1.5 flex-1 overflow-y-auto ${collapsed ? "" : "space-y-2"}`}>
          {NAV_SECTIONS.map((section, si) => (
            <div key={section.label} className={si > 0 ? "pt-2" : ""}>
              {!collapsed && <div className="pb-1 px-4 label-cap">{section.label}</div>}
              {collapsed && si > 0 && <div className="h-px bg-[var(--border)] mx-2 my-2" />}
              <div className="space-y-1">
                {section.items.map((it) => {
                  const Icon = it.icon;
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      data-testid={it.testId}
                      className={linkClass}
                      title={collapsed ? it.text : undefined}
                    >
                      <Icon size={collapsed ? 22 : 20} weight="duotone" />
                      {!collapsed && <span>{it.text}</span>}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className={`border-t border-[var(--border)] pt-3 ${collapsed ? "space-y-2" : "space-y-3"}`}>
          {!collapsed && (
            <div className="px-2">
              <div className="label-cap">conectado como</div>
              <div className="font-display font-semibold text-sm truncate">{user?.email}</div>
            </div>
          )}
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            title={collapsed ? "Cerrar sesión" : undefined}
            className={`w-full flex items-center rounded-xl text-[var(--danger)] hover:bg-[var(--danger)] hover:text-white transition text-sm font-display font-medium ${
              collapsed ? "justify-center py-2" : "gap-2 px-4 py-2"
            }`}
          >
            <SignOut size={collapsed ? 20 : 18} weight="duotone" />
            {!collapsed && "Cerrar sesión"}
          </button>
        </div>
      </aside>
      <main className="flex-1 p-4 sm:p-6 md:p-8 lg:p-12 overflow-auto min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
